import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UserCircle, ShieldCheck, Plus, PencilSimple, Trash, Printer, Check, X } from '@phosphor-icons/react';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMRForm, setShowMRForm] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const loadUsers = () => {
    api.getUsers().then(r => { setUsers(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { loadUsers(); }, []);

  const roleColors = { admin: '#007AFF', manager: '#34C759', cashier: '#FFCC00', mr: '#0A2540' };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>Staff Management</h1>
          <p className="text-sm mt-1" style={{ color: '#8A94A6' }}>{users.length} registered users</p>
        </div>
        <Button onClick={() => { setEditUser(null); setShowMRForm(true); }} className="h-9 px-4 text-sm font-semibold rounded-md" style={{ background: '#0A2540', color: 'white' }}>
          <Plus size={16} weight="bold" className="mr-1" /> Add MR / Staff
        </Button>
      </div>

      <Tabs defaultValue="staff">
        <TabsList>
          <TabsTrigger value="staff">Staff List</TabsTrigger>
          <TabsTrigger value="attendance">Attendance</TabsTrigger>
          <TabsTrigger value="salary">Salary Report</TabsTrigger>
          <TabsTrigger value="mr-app">MR App QR</TabsTrigger>
        </TabsList>

        <TabsContent value="staff" className="mt-4">
          {/* QR Code Card at top */}
          <div className="mb-4 p-4 rounded-md border flex items-center gap-6" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
            <img src={api.getMrAppQrUrl()} alt="MR App QR Code" className="w-24 h-24 rounded border" style={{ borderColor: '#E2E4E9' }} />
            <div>
              <p className="text-sm font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>MR Mobile App</p>
              <p className="text-xs mt-1" style={{ color: '#8A94A6' }}>Scan this QR code to download the PharmaPro MR App on any Android device. Share with your MR team for instant access.</p>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/PharmaPro-MR.apk`} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-xs font-semibold" style={{ color: '#007AFF' }}>
                Direct Download Link
              </a>
            </div>
          </div>
          <div className="rounded-md border overflow-hidden" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
            <table className="w-full text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              <thead><tr style={{ background: '#F9F9FA', borderBottom: '1px solid #E2E4E9' }}>
                {['Name', 'Login ID (Phone)', 'Role', 'Actions'].map(h => (
                  <th key={h} className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={4} className="px-4 py-8 text-center" style={{ color: '#8A94A6' }}>Loading...</td></tr> :
                  users.map(u => (
                    <tr key={u._id} className="hover:bg-slate-50" style={{ borderBottom: '1px solid #E2E4E9' }}>
                      <td className="px-4 py-2.5 flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: roleColors[u.role] || '#8A94A6' }}>
                          {u.name?.charAt(0)}
                        </div>
                        <span className="font-medium" style={{ color: '#0A2540' }}>{u.name}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-xs font-medium px-2 py-0.5 rounded" style={{ background: '#007AFF08', color: '#007AFF' }}>{u.phone}</span>
                      </td>
                      <td className="px-4 py-2.5"><span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase" style={{ background: `${roleColors[u.role]}15`, color: roleColors[u.role] }}>{u.role}</span></td>
                      <td className="px-4 py-2.5 flex gap-2">
                        <button onClick={() => { setEditUser(u); setShowMRForm(true); }}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold hover:bg-slate-100 transition-colors"
                          style={{ color: '#007AFF' }}>
                          <PencilSimple size={12} /> Edit / Credentials
                        </button>
                        {u.role !== 'admin' && <button onClick={async () => { if (window.confirm('Delete user?')) { await api.deleteUser(u._id); loadUsers(); }}} className="p-1 rounded hover:bg-red-50"><Trash size={14} style={{ color: '#FF3B30' }} /></button>}
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="attendance" className="mt-4"><StaffAttendance users={users} /></TabsContent>
        <TabsContent value="salary" className="mt-4"><SalaryReport users={users} /></TabsContent>
        <TabsContent value="mr-app" className="mt-4">
          <div className="max-w-md mx-auto p-8 rounded-md border text-center" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
            <img src={api.getMrAppQrUrl()} alt="MR App QR Code" className="w-48 h-48 mx-auto rounded-lg border-2" style={{ borderColor: '#E2E4E9' }} />
            <h3 className="text-lg font-bold mt-4" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>PharmaPro MR App</h3>
            <p className="text-sm mt-2" style={{ color: '#8A94A6' }}>
              Scan this QR code with any Android phone to download and install the MR Mobile App.
              The QR code always points to the latest version.
            </p>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/PharmaPro-MR.apk`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-4 px-6 py-2.5 rounded-md text-sm font-semibold text-white"
              style={{ background: '#007AFF' }}
            >
              Download APK Directly
            </a>
            <p className="text-[10px] mt-3" style={{ color: '#8A94A6' }}>Print this page and display the QR code in your office for MRs to scan easily.</p>
          </div>
        </TabsContent>
      </Tabs>

      {/* MR/Staff Form */}
      <Dialog open={showMRForm} onOpenChange={() => setShowMRForm(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editUser ? 'Edit Staff & Login Credentials' : 'Add New MR / Staff'}</DialogTitle></DialogHeader>
          <StaffForm user={editUser} onSave={() => { setShowMRForm(false); loadUsers(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StaffForm({ user, onSave }) {
  const [form, setForm] = useState({ name: user?.name || '', phone: user?.phone || '', password: '', role: user?.role || 'mr', salary: user?.salary || 0 });
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');

  const save = async () => {
    setSaving(true); setSuccess('');
    try {
      if (user?._id) {
        await api.updateUser(user._id, form);
        setSuccess('Updated successfully! New credentials are now active.');
      } else {
        await api.createMR({ ...form, role: form.role });
        setSuccess('Staff created successfully!');
      }
      setTimeout(() => onSave(), 1500);
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      {success && (
        <div className="p-3 rounded-md text-sm font-medium" style={{ background: '#F0FFF4', color: '#34C759', border: '1px solid #34C75930' }}>
          {success}
        </div>
      )}

      {/* Login Credentials Section */}
      <div className="p-4 rounded-md border-2" style={{ borderColor: '#007AFF', background: '#007AFF05' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded flex items-center justify-center" style={{ background: '#007AFF' }}>
            <ShieldCheck size={14} weight="fill" color="white" />
          </div>
          <h3 className="text-xs font-bold uppercase tracking-widest" style={{ color: '#007AFF' }}>Login Credentials</h3>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#0A2540' }}>Login ID (Phone Number)</Label>
            <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              className="h-9 mt-1 text-sm font-medium" style={{ borderColor: '#007AFF40' }}
              placeholder="Enter phone number as Login ID" />
            {user && <p className="text-[10px] mt-1" style={{ color: '#8A94A6' }}>Current: {user.phone}</p>}
          </div>

          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#0A2540' }}>
              Password {user ? '(enter new password to change)' : ''}
            </Label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                className="h-9 mt-1 text-sm font-medium pr-16" style={{ borderColor: '#007AFF40' }}
                placeholder={user ? 'Leave empty to keep current' : 'Set password'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-semibold px-2 py-1 rounded"
                style={{ color: '#007AFF', background: '#007AFF10' }}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            {user && <p className="text-[10px] mt-1" style={{ color: '#8A94A6' }}>Password is encrypted. Enter a new password to update.</p>}
          </div>
        </div>
      </div>

      {/* Staff Details */}
      <div className="space-y-3">
        <div><Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Full Name</Label>
          <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-8 mt-1" /></div>
        <div><Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Role</Label>
          <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
            <SelectTrigger className="h-8 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="mr">Medical Representative</SelectItem>
              <SelectItem value="cashier">Cashier</SelectItem>
              <SelectItem value="manager">Manager</SelectItem>
            </SelectContent>
          </Select></div>
        <div><Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Monthly Salary</Label>
          <Input type="number" value={form.salary} onChange={e => setForm(f => ({ ...f, salary: Number(e.target.value) }))} className="h-8 mt-1" /></div>
      </div>

      <Button onClick={save} disabled={saving} className="w-full h-10 text-sm font-bold" style={{ background: '#0A2540', color: 'white' }}>
        {saving ? 'Saving...' : user ? 'Update Staff & Credentials' : 'Create Staff'}
      </Button>
    </div>
  );
}

function StaffAttendance({ users }) {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.getStaffAttendance(month).then(r => { setAttendance(r.data); setLoading(false); }); }, [month]);

  const markAtt = async (userId, userName, date, status) => {
    await api.markStaffAttendance({ user_id: userId, user_name: userName, date, status });
    api.getStaffAttendance(month).then(r => setAttendance(r.data));
  };

  const today = new Date().toISOString().split('T')[0];
  const staffUsers = users.filter(u => u.role !== 'admin');

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Month</Label>
          <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="h-8 mt-1 w-48" />
        </div>
      </div>

      {/* Mark today's attendance */}
      <div className="rounded-md border p-4" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
        <h3 className="text-sm font-semibold mb-3" style={{ color: '#0A2540' }}>Mark Today's Attendance ({today})</h3>
        <div className="space-y-2">
          {staffUsers.map(u => {
            const todayAtt = attendance.find(a => a.user_id === u._id && a.date === today);
            return (
              <div key={u._id} className="flex items-center justify-between p-2 rounded hover:bg-slate-50" style={{ borderBottom: '1px solid #E2E4E9' }}>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium" style={{ color: '#0A2540' }}>{u.name}</span>
                  <span className="text-xs" style={{ color: '#8A94A6' }}>({u.role})</span>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => markAtt(u._id, u.name, today, 'present')} className="px-2 py-1 rounded text-[10px] font-bold"
                    style={{ background: todayAtt?.status === 'present' ? '#34C759' : '#F0F4F8', color: todayAtt?.status === 'present' ? 'white' : '#8A94A6' }}>P</button>
                  <button onClick={() => markAtt(u._id, u.name, today, 'absent')} className="px-2 py-1 rounded text-[10px] font-bold"
                    style={{ background: todayAtt?.status === 'absent' ? '#FF3B30' : '#F0F4F8', color: todayAtt?.status === 'absent' ? 'white' : '#8A94A6' }}>A</button>
                  <button onClick={() => markAtt(u._id, u.name, today, 'half-day')} className="px-2 py-1 rounded text-[10px] font-bold"
                    style={{ background: todayAtt?.status === 'half-day' ? '#FFCC00' : '#F0F4F8', color: todayAtt?.status === 'half-day' ? 'white' : '#8A94A6' }}>H</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary table */}
      <div className="rounded-md border overflow-hidden" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
        <table className="w-full text-sm">
          <thead><tr style={{ background: '#F9F9FA' }}>
            {['Staff', 'Present', 'Absent', 'Half-Day'].map(h => (
              <th key={h} className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {staffUsers.map(u => {
              const userAtt = attendance.filter(a => a.user_id === u._id);
              return (
                <tr key={u._id} style={{ borderBottom: '1px solid #E2E4E9' }}>
                  <td className="px-4 py-2 font-medium">{u.name}</td>
                  <td className="px-4 py-2" style={{ color: '#34C759', fontVariantNumeric: 'tabular-nums' }}>{userAtt.filter(a => a.status === 'present').length}</td>
                  <td className="px-4 py-2" style={{ color: '#FF3B30', fontVariantNumeric: 'tabular-nums' }}>{userAtt.filter(a => a.status === 'absent').length}</td>
                  <td className="px-4 py-2" style={{ color: '#FFCC00', fontVariantNumeric: 'tabular-nums' }}>{userAtt.filter(a => a.status === 'half-day').length}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SalaryReport({ users }) {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [salaries, setSalaries] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showDeductionForm, setShowDeductionForm] = useState(false);
  const [deductions, setDeductions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([api.getSalaries(month), api.getDeductions('', month)]).then(([sRes, dRes]) => {
      setSalaries(sRes.data); setDeductions(dRes.data); setLoading(false);
    });
  };
  useEffect(() => { load(); }, [month]);

  const staffUsers = users.filter(u => u.role !== 'admin');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Month</Label>
          <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="h-8 mt-1 w-48" />
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowDeductionForm(true)} variant="outline" className="h-9 px-4 text-sm font-semibold rounded-md" style={{ borderColor: '#FF3B30', color: '#FF3B30' }}>
            <Trash size={16} className="mr-1" /> Deduction
          </Button>
          <Button onClick={() => setShowAddForm(true)} className="h-9 px-4 text-sm font-semibold rounded-md" style={{ background: '#0A2540', color: 'white' }}>
            <Plus size={16} className="mr-1" /> Add Salary
          </Button>
        </div>
      </div>

      <div className="rounded-md border overflow-hidden" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
        <table className="w-full text-sm">
          <thead><tr style={{ background: '#F9F9FA' }}>
            {['Staff', 'Basic', 'Incentives', 'Fuel', 'Deductions', 'Net Salary', 'Remarks', 'Actions'].map(h => (
              <th key={h} className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {salaries.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center" style={{ color: '#8A94A6' }}>No salary records for {month}</td></tr>
            ) : salaries.map((s, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #E2E4E9' }}>
                <td className="px-3 py-2 font-medium">{s.user_name}</td>
                <td className="px-3 py-2" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{s.basic_salary?.toLocaleString()}</td>
                <td className="px-3 py-2" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{s.incentives?.toLocaleString()}</td>
                <td className="px-3 py-2" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{s.fuel_allowance?.toLocaleString()}</td>
                <td className="px-3 py-2" style={{ color: '#FF3B30', fontVariantNumeric: 'tabular-nums' }}>-₹{s.deductions?.toLocaleString()}</td>
                <td className="px-3 py-2 font-bold" style={{ color: '#0A2540', fontVariantNumeric: 'tabular-nums' }}>₹{s.net_salary?.toLocaleString()}</td>
                <td className="px-3 py-2 text-xs" style={{ color: '#8A94A6' }}>{s.remarks}</td>
                <td className="px-3 py-2">
                  <a href={api.getSalarySlipPdf(s._id)} target="_blank" rel="noopener noreferrer" className="text-xs font-medium hover:underline" style={{ color: '#007AFF' }}>
                    <Printer size={14} className="inline mr-1" />PDF
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Deduction History */}
      {deductions.length > 0 && (
        <div className="rounded-md border overflow-hidden" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
          <div className="px-4 py-2 border-b" style={{ borderColor: '#E2E4E9', background: '#FFF0F0' }}>
            <h3 className="text-xs font-bold" style={{ color: '#FF3B30' }}>Deductions for {month} ({deductions.length})</h3>
          </div>
          <table className="w-full text-xs">
            <thead><tr style={{ background: '#F9F9FA' }}>
              {['Date', 'Employee', 'Amount', 'Remarks'].map(h => (
                <th key={h} className="text-left px-3 py-1.5 text-[9px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {deductions.map((d, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #E2E4E9' }}>
                  <td className="px-3 py-1.5">{d.date}</td>
                  <td className="px-3 py-1.5 font-medium">{d.user_name}</td>
                  <td className="px-3 py-1.5 font-bold" style={{ color: '#FF3B30', fontVariantNumeric: 'tabular-nums' }}>-₹{d.amount?.toLocaleString()}</td>
                  <td className="px-3 py-1.5" style={{ color: '#8A94A6' }}>{d.remarks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showAddForm} onOpenChange={() => setShowAddForm(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Salary</DialogTitle></DialogHeader>
          <SalaryForm month={month} users={staffUsers} onSave={() => { setShowAddForm(false); load(); }} />
        </DialogContent>
      </Dialog>

      <Dialog open={showDeductionForm} onOpenChange={() => setShowDeductionForm(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle style={{ color: '#FF3B30' }}>Record Deduction</DialogTitle></DialogHeader>
          <DeductionForm month={month} users={staffUsers} onSave={() => { setShowDeductionForm(false); load(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DeductionForm({ month, users, onSave }) {
  const [form, setForm] = useState({ user_id: '', user_name: '', month, amount: 0, remarks: '', date: new Date().toISOString().split('T')[0] });
  const save = async () => {
    if (!form.user_id || form.amount <= 0) return;
    try { await api.addDeduction(form); onSave(); } catch {}
  };
  return (
    <div className="space-y-3">
      <div><Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Employee</Label>
        <Select value={form.user_id} onValueChange={v => { const u = users.find(x => x._id === v); setForm(f => ({ ...f, user_id: v, user_name: u?.name || '' })); }}>
          <SelectTrigger className="h-8 mt-1"><SelectValue placeholder="Select employee" /></SelectTrigger>
          <SelectContent>{users.map(u => <SelectItem key={u._id} value={u._id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
        </Select></div>
      <div><Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Date</Label>
        <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="h-8 mt-1" /></div>
      <div><Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Deduction Amount</Label>
        <Input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: Number(e.target.value) }))} className="h-8 mt-1" /></div>
      <div><Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Remarks</Label>
        <Input value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} className="h-8 mt-1" placeholder="e.g. Salary advance, Late penalty" /></div>
      <Button onClick={save} className="w-full h-9 text-sm font-semibold" style={{ background: '#FF3B30', color: 'white' }}>
        Record Deduction of ₹{form.amount.toLocaleString()}
      </Button>
    </div>
  );
}

function SalaryForm({ month, users, onSave }) {
  const [form, setForm] = useState({ user_id: '', user_name: '', month, basic_salary: 0, incentives: 0, fuel_allowance: 0, deductions: 0, remarks: '' });
  const save = async () => {
    try { await api.addSalary(form); onSave(); } catch {}
  };
  return (
    <div className="space-y-3">
      <div><Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Staff</Label>
        <Select value={form.user_id} onValueChange={v => { const u = users.find(x => x._id === v); setForm(f => ({ ...f, user_id: v, user_name: u?.name || '' })); }}>
          <SelectTrigger className="h-8 mt-1"><SelectValue placeholder="Select staff" /></SelectTrigger>
          <SelectContent>{users.map(u => <SelectItem key={u._id} value={u._id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
        </Select></div>
      {[
        { k: 'basic_salary', label: 'Basic Salary' }, { k: 'incentives', label: 'Incentives' },
        { k: 'fuel_allowance', label: 'Fuel Allowance' }, { k: 'deductions', label: 'Deductions' },
      ].map(f => (
        <div key={f.k}><Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{f.label}</Label>
          <Input type="number" value={form[f.k]} onChange={e => setForm(fo => ({ ...fo, [f.k]: Number(e.target.value) }))} className="h-8 mt-1" /></div>
      ))}
      <div><Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Remarks</Label>
        <Input value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} className="h-8 mt-1" /></div>
      <div className="p-2 rounded bg-slate-50 text-sm font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>
        Net: ₹{(form.basic_salary + form.incentives + form.fuel_allowance - form.deductions).toLocaleString()}
      </div>
      <Button onClick={save} className="w-full h-9 text-sm font-semibold" style={{ background: '#0A2540', color: 'white' }}>Save Salary</Button>
    </div>
  );
}
